import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertyAPI } from '../services/propertyAPI';
import PropertyCard from '../components/PropertyCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PROPERTY_TYPES, BHK_OPTIONS, FURNISHING_OPTIONS, PARKING_OPTIONS, RENTAL_TYPE_OPTIONS, SORT_OPTIONS } from '../utils/propertyConstants';

const Home = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Quick filter states
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedBudget, setSelectedBudget] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showBudgetDropdown, setShowBudgetDropdown] = useState(false);
  const [searchBarHeight, setSearchBarHeight] = useState(120);
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    // Reset and fetch when filters or sort change
    setProperties([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchProperties(1, true);
    // eslint-disable-next-line
  }, [filters, sortBy]);

  useEffect(() => {
    // Calculate search bar height for proper filter pills positioning
    const updateSearchBarHeight = () => {
      setTimeout(() => {
        const searchBar = document.getElementById('search-bar-container');
        if (searchBar) {
          const height = searchBar.offsetHeight;
          setSearchBarHeight(height);
        }
      }, 100);
    };

    updateSearchBarHeight();
    window.addEventListener('resize', updateSearchBarHeight);
    return () => window.removeEventListener('resize', updateSearchBarHeight);
  }, [selectedCity, selectedType, selectedBudget, showTypeDropdown, showBudgetDropdown]);

  useEffect(() => {
    // Infinite scroll event listener
    const handleScroll = () => {
      if (loading || loadingMore || !hasMore) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // Load more when user is 300px from bottom
      if (scrollTop + clientHeight >= scrollHeight - 300) {
        loadMoreProperties();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line
  }, [loading, loadingMore, hasMore, currentPage]);

  const fetchProperties = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      }
      setError('');
      
      let response;
      if (searchQuery) {
        response = await propertyAPI.searchProperties(searchQuery, filters, page);
      } else {
        response = await propertyAPI.getProperties(filters, page);
      }

      if (response.success) {
        if (reset) {
          setProperties(response.data);
        } else {
          setProperties(prev => [...prev, ...response.data]);
        }
        setPagination(response.pagination);
        setHasMore(response.pagination.hasNext);
      }
    } catch (err) {
      setError('Failed to fetch properties. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreProperties = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      
      const filtersWithSort = { ...filters, sort: sortBy };
      let response;
      if (searchQuery) {
        response = await propertyAPI.searchProperties(searchQuery, filtersWithSort, nextPage);
      } else {
        response = await propertyAPI.getProperties(filtersWithSort, nextPage);
      }

      if (response.success) {
        setProperties(prev => [...prev, ...response.data]);
        setPagination(response.pagination);
        setCurrentPage(nextPage);
        setHasMore(response.pagination.hasNext);
      }
    } catch (err) {
      console.error('Failed to load more properties:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleQuickSearch = () => {
    const newFilters = {};
    if (selectedCity) newFilters.city = selectedCity;
    if (selectedType) newFilters.type = selectedType;
    if (selectedBudget) newFilters.maxPrice = selectedBudget;
    
    setFilters(newFilters);
    setShowTypeDropdown(false);
    setShowBudgetDropdown(false);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowTypeDropdown(false);
        setShowBudgetDropdown(false);
      }
    };
    if (showTypeDropdown || showBudgetDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTypeDropdown, showBudgetDropdown]);

  const handleApplyMoreFilters = (moreFilters) => {
    setFilters({ ...filters, ...moreFilters });
    setShowMoreFilters(false);
  };

  const removeFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
  };

  return (
    <div className="min-h-screen gradient-bg-light pt-20">
      {/* Fixed Header */}
      <Header transparent={true} />

      {/* Enhanced Search Bar */}
      <div className="bg-white border-b border-purple-100 fixed top-[80px] left-0 right-0 z-40 shadow-sm">
        <div 
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${Object.keys(filters).length > 0 ? 'py-3 sm:py-5' : 'py-2 sm:py-3'}`}
          id="search-bar-container"
        >
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-white border-2 border-purple-200 rounded-full px-3 sm:px-4 py-2 sm:py-2 focus-within:border-primary focus-within:ring-4 focus-within:ring-purple-100 transition-all duration-300 shadow-md hover:shadow-lg">
            {/* Location Input */}
            <div className="flex items-center flex-1 border-r-0 sm:border-r border-gray-300 pr-0 sm:pr-4 pb-2 sm:pb-0 border-b sm:border-b-0">
              <svg className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <input
                type="text"
                placeholder="Enter City or Locality"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="flex-1 outline-none text-gray-900 placeholder-gray-500 text-sm sm:text-base"
              />
            </div>

            {/* Property Type Dropdown */}
            <div className="relative flex items-center flex-1 border-r-0 sm:border-r border-gray-300 pr-0 sm:pr-4 pb-2 sm:pb-0 border-b sm:border-b-0 dropdown-container">
              <svg className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <button
                onClick={() => {
                  setShowTypeDropdown(!showTypeDropdown);
                  setShowBudgetDropdown(false);
                }}
                className="flex-1 outline-none text-gray-900 bg-transparent cursor-pointer text-left flex items-center justify-between text-sm sm:text-base"
              >
                <span className={selectedType ? 'text-gray-900' : 'text-gray-500'}>
                  {selectedType ? PROPERTY_TYPES.find(t => t.value === selectedType)?.label : 'Property Type'}
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${showTypeDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showTypeDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] max-h-60 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedType('');
                      setShowTypeDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-700 border-b border-gray-100"
                  >
                    All Types
                  </button>
                  {PROPERTY_TYPES.map(type => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setSelectedType(type.value);
                        setShowTypeDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition ${
                        selectedType === type.value ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Budget Dropdown */}
            <div className="relative flex items-center flex-1 border-r-0 sm:border-r border-gray-300 pr-0 sm:pr-4 pb-2 sm:pb-0 border-b sm:border-b-0 dropdown-container">
              <svg className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <button
                onClick={() => {
                  setShowBudgetDropdown(!showBudgetDropdown);
                  setShowTypeDropdown(false);
                }}
                className="flex-1 outline-none text-gray-900 bg-transparent cursor-pointer text-left flex items-center justify-between text-sm sm:text-base"
              >
                <span className={selectedBudget ? 'text-gray-900' : 'text-gray-500'}>
                  {selectedBudget 
                    ? `Up to ₹${parseInt(selectedBudget).toLocaleString('en-IN')}`
                    : 'Budget'
                  }
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${showBudgetDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showBudgetDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] max-h-60 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedBudget('');
                      setShowBudgetDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-700 border-b border-gray-100"
                  >
                    All Budgets
                  </button>
                  <button
                    onClick={() => {
                      setSelectedBudget('25000');
                      setShowBudgetDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition ${
                      selectedBudget === '25000' ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    Up to ₹25,000
                  </button>
                  <button
                    onClick={() => {
                      setSelectedBudget('50000');
                      setShowBudgetDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition ${
                      selectedBudget === '50000' ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    Up to ₹50,000
                  </button>
                  <button
                    onClick={() => {
                      setSelectedBudget('100000');
                      setShowBudgetDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition ${
                      selectedBudget === '100000' ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    Up to ₹1,00,000
                  </button>
                  <button
                    onClick={() => {
                      setSelectedBudget('200000');
                      setShowBudgetDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition ${
                      selectedBudget === '200000' ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    Up to ₹2,00,000
                  </button>
                  <button
                    onClick={() => {
                      setSelectedBudget('500000');
                      setShowBudgetDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition ${
                      selectedBudget === '500000' ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    ₹5,00,000+
                  </button>
                </div>
              )}
            </div>

            {/* More Filters Button */}
            <button
              onClick={() => setShowMoreFilters(!showMoreFilters)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full font-semibold transition flex-shrink-0 border-r-0 sm:border-r border-gray-300 pr-0 sm:pr-4 pb-2 sm:pb-0 border-b sm:border-b-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              <span className="text-sm sm:text-base">More Filters</span>
            </button>

            {/* Search Button */}
            <button
              onClick={handleQuickSearch}
              className="px-4 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-primary to-purple-600 text-white rounded-full font-bold hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex-shrink-0 btn-ripple text-sm sm:text-base w-full sm:w-auto"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Filter Pills */}
      {Object.keys(filters).length > 0 && (
        <div className="bg-white border-b border-purple-100 fixed left-0 right-0 z-30 shadow-sm" style={{ top: 'calc(80px + var(--search-bar-height, 120px))' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-3">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Active Filters */}
              {filters.city && (
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-primary text-primary rounded-full font-semibold shadow-sm hover:shadow-md transition-all text-sm sm:text-base">
                  <span>{filters.city}</span>
                  <button onClick={() => removeFilter('city')} className="hover:bg-primary hover:text-white rounded-full p-0.5 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {filters.type && (
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-primary text-primary rounded-full font-semibold shadow-sm hover:shadow-md transition-all text-sm sm:text-base">
                  <span>{PROPERTY_TYPES.find(t => t.value === filters.type)?.label}</span>
                  <button onClick={() => removeFilter('type')} className="hover:bg-primary hover:text-white rounded-full p-0.5 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {filters.bhk && (
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-primary text-primary rounded-full font-semibold shadow-sm hover:shadow-md transition-all text-sm sm:text-base">
                  <span>{filters.bhk} BHK</span>
                  <button onClick={() => removeFilter('bhk')} className="hover:bg-primary hover:text-white rounded-full p-0.5 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {filters.furnishing && (
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-primary text-primary rounded-full font-semibold shadow-sm hover:shadow-md transition-all text-sm sm:text-base">
                  <span>{FURNISHING_OPTIONS.find(f => f.value === filters.furnishing)?.label}</span>
                  <button onClick={() => removeFilter('furnishing')} className="hover:bg-primary hover:text-white rounded-full p-0.5 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {filters.parking && (
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-primary text-primary rounded-full font-semibold shadow-sm hover:shadow-md transition-all text-sm sm:text-base">
                  <span>{PARKING_OPTIONS.find(p => p.value === filters.parking)?.label}</span>
                  <button onClick={() => removeFilter('parking')} className="hover:bg-primary hover:text-white rounded-full p-0.5 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {filters.rentalType && (
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-primary text-primary rounded-full font-semibold shadow-sm hover:shadow-md transition-all text-sm sm:text-base">
                  <span>{RENTAL_TYPE_OPTIONS.find(r => r.value === filters.rentalType)?.label}</span>
                  <button onClick={() => removeFilter('rentalType')} className="hover:bg-primary hover:text-white rounded-full p-0.5 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {filters.bachelorAllowed && (
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-primary text-primary rounded-full font-semibold shadow-sm hover:shadow-md transition-all text-sm sm:text-base">
                  <span>Bachelor Allowed</span>
                  <button onClick={() => removeFilter('bachelorAllowed')} className="hover:bg-primary hover:text-white rounded-full p-0.5 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              <button
                onClick={() => {
                  setFilters({});
                  setSelectedCity('');
                  setSelectedType('');
                  setSelectedBudget('');
                  setSortBy('recent');
                }}
                className="text-primary hover:text-purple-700 font-semibold ml-2 text-sm sm:text-base"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* More Filters Modal */}
      {showMoreFilters && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-start justify-center pt-20 sm:pt-24 fade-in" onClick={() => setShowMoreFilters(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[85vh] overflow-y-auto scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">More Filters</h3>
              <button onClick={() => setShowMoreFilters(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* BHK Filter */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">BHK</label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {BHK_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setFilters({ ...filters, bhk: option.value })}
                      className={`px-4 py-2 rounded-lg font-semibold transition ${
                        filters.bhk === option.value
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Furnishing Filter */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">Furnishing</label>
                <div className="grid grid-cols-3 gap-2">
                  {FURNISHING_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setFilters({ ...filters, furnishing: option.value })}
                      className={`px-4 py-2 rounded-lg font-semibold transition ${
                        filters.furnishing === option.value
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Parking Filter */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">Parking</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {PARKING_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setFilters({ ...filters, parking: option.value })}
                      className={`px-4 py-2 rounded-lg font-semibold transition ${
                        filters.parking === option.value
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">Price Range</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="number"
                      placeholder="Min Price"
                      value={filters.minPrice || ''}
                      onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      placeholder="Max Price"
                      value={filters.maxPrice || ''}
                      onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Area Range */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">Area (Sq. Ft.)</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="number"
                      placeholder="Min Area"
                      value={filters.minArea || ''}
                      onChange={(e) => setFilters({ ...filters, minArea: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      placeholder="Max Area"
                      value={filters.maxArea || ''}
                      onChange={(e) => setFilters({ ...filters, maxArea: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Rental Type Filter */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">Rental Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {RENTAL_TYPE_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setFilters({ ...filters, rentalType: filters.rentalType === option.value ? '' : option.value })}
                      className={`px-4 py-2 rounded-lg font-semibold transition ${
                        filters.rentalType === option.value
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bachelor Allowed Filter */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">Bachelor Allowed</label>
                <button
                  type="button"
                  onClick={() => setFilters({ ...filters, bachelorAllowed: filters.bachelorAllowed ? '' : 'true' })}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    filters.bachelorAllowed
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filters.bachelorAllowed ? 'Yes' : 'Filter: Bachelor Allowed'}
                </button>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-4">
              <button
                onClick={() => {
                  setFilters({});
                  setShowMoreFilters(false);
                }}
                className="px-6 py-3 bg-gray-100 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 hover:border-gray-400 transition-all duration-200"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowMoreFilters(false)}
                className="px-6 py-3 bg-gradient-to-r from-primary to-purple-600 text-white rounded-xl font-bold hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200 btn-ripple"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300"
        style={{ 
          marginTop: Object.keys(filters).length > 0 
            ? `${80 + searchBarHeight + 10}px` 
            : `${80 + searchBarHeight - 30}px`,
          paddingTop: Object.keys(filters).length > 0 ? '1rem' : '0',
          paddingBottom: '2rem'
        }}
      >
        {/* Properties Grid - Full Width */}
        <div>
          {/* Results Header */}
          <div className={Object.keys(filters).length > 0 ? "mb-4" : "mb-2"}>
            <h1 className="text-2xl font-bold text-gray-900">
              Properties ({pagination.totalCount || 0})
            </h1>
            {pagination.totalCount !== undefined && (
              <p className="text-gray-600 mt-1">
                {pagination.totalCount} results | Properties in India
              </p>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Properties Grid - Full Width 3 Columns */}
          {!loading && !error && properties.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <PropertyCard key={property._id || property.id} property={property} />
              ))}
            </div>
          )}

          {/* No Results */}
          {!loading && !error && properties.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No properties found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your filters or search criteria</p>
              <button
                onClick={() => {
                  setFilters({});
                  setSelectedCity('');
                  setSelectedType('');
                  setSelectedBudget('');
                  setSortBy('recent');
                }}
                className="px-6 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-purple-700 transition"
              >
                Clear Filters
              </button>
            </div>
          )}

          {/* Infinite Scroll Loading Indicator */}
          {loadingMore && (
            <div className="mt-8 flex flex-col items-center justify-center space-y-3 py-8">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200"></div>
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent absolute top-0 left-0"></div>
              </div>
              <p className="text-gray-600 font-medium">Loading more properties...</p>
            </div>
          )}

          {/* End of Results */}
          {!loading && !loadingMore && !hasMore && properties.length > 0 && (
            <div className="mt-8 text-center py-8 border-t border-gray-200 fade-in">
              <div className="inline-flex items-center space-x-2 text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium">You've seen all {pagination.totalCount} properties</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;

